import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { LoadingSpinner } from '../ui/loading-spinner';
import StripeCheckout from './StripeCheckout';
import { ChevronDown, ChevronUp, Info, RefreshCw, Settings, RotateCcw, Star, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Badge } from '../ui/badge';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

/**
 * Format timestamp for display with optional paid indicator
 */
const formatSubscriptionDate = (timestamp, isPaid = false) => {
  if (!timestamp) return 'N/A';
  const numericTimestamp = Number(timestamp);
  if (isNaN(numericTimestamp)) return 'Invalid Date';
  
  const milliseconds = numericTimestamp * (numericTimestamp < 10000000000 ? 1000 : 1);
  const date = new Date(milliseconds);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const formattedDate = date.toLocaleDateString();
  return isPaid ? `${formattedDate} (Paid subscription)` : formattedDate;
};

/**
 * Format the date helper function for cancellation notices
 */
const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  try {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    return 'Unknown';
  }
};

const ActiveSubscriptionDisplay = ({ 
  subscription,
  loading,
  refreshing,
  onRefresh,
  onManageSubscription,
  onUpgradeToPro,
  onSelectPlan,
  onReactivateSubscription,
  stripeCustomerId
}) => {
  const { theme } = useTheme();
  
  // Component state
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);
  const [showTrialLossModal, setShowTrialLossModal] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [showProDetails, setShowProDetails] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // Subscription status calculations
  const isPro = subscription?.level === 'pro';
  const isTrial = subscription?.status === 'trialing';
  const isBasicTrial = isTrial && subscription?.level === 'basic';
  const isBasicActive = !isTrial && subscription?.level === 'basic';
  
  // Check if user can upgrade (Basic to Pro trial)
  const canUpgrade = subscription?.level === 'basic' && 
                     subscription?.isFreeTrial && 
                     !subscription?.isPaid;
  // Enhanced subscription status detection
  const isCancelling = subscription?.cancelAtPeriodEnd && subscription?.status === 'active';
  const isBasicSubscription = !isTrial && subscription?.level === 'basic' && subscription?.status === 'active';
  
  // Calculate days remaining for trial users
  const daysRemaining = useMemo(() => {
    if (!isTrial || !subscription?.trialEnd) return 0;
    const trialEndDate = new Date(subscription.trialEnd * 1000);
    const now = new Date();
    const diffTime = trialEndDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [isTrial, subscription?.trialEnd]);

  // Separate messaging for trial vs paid subscriptions
  const statusText = isCancelling ? 'Cancelling at Period End' : 
                     isTrial ? `${daysRemaining} Days Left` : 'Active';
  const statusColor = isCancelling ? 'text-orange-600 dark:text-orange-400' :
                      isTrial ? 'text-blue-600 dark:text-blue-400' : 
                      'text-green-600 dark:text-green-400';

  const planLevel = subscription?.level || 'PRO';
  const planName = planLevel.charAt(0).toUpperCase() + planLevel.slice(1).toLowerCase();
  
  // Update header text to differentiate trial vs subscription
  const headerText = isTrial ? `Your ${planName} Trial` : `Your ${planName} Subscription`;

  // Date formatting
  const isCancelled = subscription?.cancelAtPeriodEnd || subscription?.canceledAt;
  const periodEnd = formatSubscriptionDate(subscription?.currentPeriodEnd, !isTrial && !isCancelled);
  const trialEnd = formatSubscriptionDate(subscription?.trialEnd);
  
  // Function to handle upgrade to Pro
  const handleUpgradeToProTrial = () => {
    if (onUpgradeToPro) {
      onUpgradeToPro();
    } else if (onSelectPlan) {
      onSelectPlan('pro', true);
    }
  };

  // Handle Pro upgrade for Basic subscription users
  const handleUpgradeToProSubscription = async () => {
    setUpgradeLoading(true);
    try {
      if (onSelectPlan) {
        await onSelectPlan('pro', false);
      }
    } catch (error) {
      console.error('Error upgrading to pro:', error);
      setUpgradeLoading(false);
    }
    // Don't reset loading here as we're redirecting to Stripe
  };

  // Intercept subscribe action if on trial
  const handlePlanSelect = (planId, isTrial) => {
    if (isTrial || !isTrial) {
      // Only intercept if user is on trial and is subscribing (not upgrading trial)
      if (isTrial && subscription?.status === 'trialing' && daysRemaining > 0) {
        setPendingPlanId(planId);
        setShowTrialLossModal(true);
        return;
      }
    }
    if (onSelectPlan) onSelectPlan(planId, isTrial);
  };

  return (
    <div className="space-y-6">
      {/* Modal for trial loss warning */}
      <Dialog open={showTrialLossModal} onOpenChange={setShowTrialLossModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Warning: Trial Days Will Be Lost</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm">
            You have <span className="font-bold">{daysRemaining}</span> day{daysRemaining !== 1 ? 's' : ''} left of your free trial. <br />
            Subscribing now will start your paid plan immediately and you will lose your remaining trial days.<br />
            Are you sure you want to continue?
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrialLossModal(false)}>Cancel</Button>
            <button
              className="ml-2 px-4 py-2 rounded font-semibold shadow bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white"
              type="button"
              onClick={() => {
                setShowTrialLossModal(false);
                if (onSelectPlan && pendingPlanId) onSelectPlan(pendingPlanId, false);
                setPendingPlanId(null);
              }}
            >
              Continue & Subscribe
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active Subscription Header */}
      <Card className={cn(
        "border-2 shadow-lg",
        theme === 'light' 
          ? 'bg-white border-gray-200' 
          : 'bg-[#2A2A3E] border-gray-700'
      )}>
        <CardHeader className="pb-3 sm:pb-4">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <span className="text-base sm:text-lg font-semibold">{headerText}</span>
            <Badge variant="default" className={cn(
              "text-xs sm:text-sm w-fit",
              isCancelling ? 'bg-orange-500' : 
              isTrial ? 'bg-blue-500' : 'bg-green-500'
            )}>
              {statusText}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Plan</p>
              <p className="text-base sm:text-lg font-semibold leading-tight">{planName} Plan</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Status</p>
              <p className={cn("text-base sm:text-lg font-semibold leading-tight", statusColor)}>
                {statusText}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                {isCancelling ? 'Access Ends' : 
                 isTrial ? 'Trial Ends' : 'Next Billing'}
              </p>
              <p className="text-base sm:text-lg font-semibold leading-tight">
                {isTrial ? trialEnd : periodEnd}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Features</p>
              <p className="text-base sm:text-lg font-semibold leading-tight">All {planName} Features</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {isCancelling ? (
          <Button 
            onClick={() => onSelectPlan(subscription?.level || 'pro', false)}
            disabled={loading || refreshing}
            className="w-full sm:flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 min-h-[52px] py-4 px-6 text-sm sm:text-base font-medium"
            size="lg"
          >
            <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="whitespace-nowrap">Reactivate Subscription</span>
          </Button>
        ) : canUpgrade ? (
          <div className="w-full sm:flex-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={handleUpgradeToProTrial}
                    disabled={loading || refreshing}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 min-h-[52px] py-4 px-6 text-sm sm:text-base font-medium"
                    size="lg"
                  >
                    <Star className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-center">Upgrade to Pro Trial ({daysRemaining} days left)</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Upgrade to Pro features for the remaining {daysRemaining} days of your trial. No extension to trial period.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Transparency note for trial upgrade */}
            <p className="text-xs text-muted-foreground mt-2 text-center px-2">
              Upgrading to Pro trial does not extend your trial period. After your trial, uninterrupted access requires a paid subscription.
            </p>
          </div>
        ) : isBasicSubscription ? (
          <>
            <Button 
              onClick={handleUpgradeToProSubscription}
              disabled={loading || refreshing || upgradeLoading}
              className="w-full sm:flex-1 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 min-h-[52px] py-4 px-6 text-sm sm:text-base font-medium"
              size="lg"
            >
              {upgradeLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm sm:text-base whitespace-nowrap">Opening Payment Portal...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Star className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm sm:text-base font-medium whitespace-nowrap">Upgrade to PRO</span>
                </div>
              )}
            </Button>
            <p className="text-xs text-red-600 dark:text-red-400 mt-1 text-center">
              Upgrades are not prorated. You will be charged the full PRO price.
            </p>
          </>
        ) : null}
        
        {!isCancelling && !isTrial && (
          <Button 
            onClick={onManageSubscription}
            disabled={loading || refreshing || !stripeCustomerId || upgradeLoading}
            variant="outline" 
            size="lg"
            className="w-full sm:w-auto text-gray-900 dark:text-white min-h-[52px] py-4 px-6 text-sm sm:text-base font-medium"
          >
            <Settings className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="whitespace-nowrap">Manage Subscription</span>
          </Button>
        )}
        
        <Button
          onClick={onRefresh}
          disabled={loading || refreshing || upgradeLoading}
          variant="outline" 
          size="lg"
          className="w-full sm:w-auto text-gray-900 dark:text-white min-h-[52px] py-4 px-6 text-sm sm:text-base font-medium"
        >
          <RotateCcw className={cn("w-4 h-4 mr-2 flex-shrink-0", refreshing && "animate-spin")} />
          <span className="whitespace-nowrap">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
        </Button>
      </div>

      {/* Cancellation Notice */}
      {isCancelling && (
        <div className={cn(
          "p-3 sm:p-4 rounded-lg border",
          theme === 'light' 
            ? 'bg-orange-50 border-orange-200' 
            : 'bg-orange-950/30 border-orange-700'
        )}>
          <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2 text-sm sm:text-base">
            Subscription Cancelling
          </h4>
          <p className="text-orange-700 dark:text-orange-300 text-xs sm:text-sm leading-relaxed">
            Your subscription will end on{' '}
            <span className="font-semibold whitespace-nowrap">{formatDate(subscription.currentPeriodEnd)}</span>.
            You can reactivate it at any time before this date to continue enjoying premium features.
          </p>
        </div>
      )}

      {(isTrial || showFeatureComparison) && (
        <div className="space-y-4">
          {isTrial && (
            <Alert variant="info" className="border-2">
              <Info className="h-4 w-4 mr-2 sm:mr-3 flex-shrink-0" />
              <span className="text-gray-900 dark:text-white text-sm sm:text-base leading-relaxed">
                {subscription?.level === 'pro' 
                  ? `You have ${daysRemaining} days remaining on your Pro trial. When your trial ends, you can subscribe to continue with Pro features or choose Basic.`
                  : `You have ${daysRemaining} days remaining on your Basic trial. Upgrade to Pro trial to access more features, or subscribe when your trial ends.`
                }
              </span>
            </Alert>
          )}

          {/* Subscription Options Banner for Trial Users */}
          {isTrial && (
            <div className={cn(
              "p-3 sm:p-4 rounded-lg border",
              theme === 'light' 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-amber-950/30 border-amber-700'
            )}>
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-start sm:items-center text-sm sm:text-base">
                <Info className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                <span>Subscribe to continue access after your trial</span>
              </h4>
              <p className="text-amber-700 dark:text-amber-300 text-xs sm:text-sm leading-relaxed">
                Choose a subscription plan below to continue accessing features after your {daysRemaining}-day trial expires.<br />
                <span className="font-semibold">Important:</span> If you subscribe before your trial ends, your paid plan will start immediately and you will lose your remaining trial days.
              </p>
            </div>
          )}
          
          <Card className={cn(
            "border",
            theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
          )}>
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className={cn(
                "text-base sm:text-lg",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                {isTrial ? 'Subscription Plans' : 'Plan Comparison'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div 
                className="w-full"
                style={{
                  '--stripe-button-styles': `
                    button {
                      width: 100% !important;
                      min-height: 52px !important;
                      padding: 16px 24px !important;
                      font-size: 14px !important;
                      font-weight: 500 !important;
                      white-space: normal !important;
                      line-height: 1.2 !important;
                    }
                    @media (min-width: 640px) {
                      button {
                        font-size: 16px !important;
                      }
                    }
                  `
                }}
              >
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .stripe-checkout-wrapper button {
                      width: 100% !important;
                      min-height: 52px !important;
                      padding: 16px 24px !important;
                      font-size: 12px !important;
                      font-weight: 500 !important;
                      white-space: normal !important;
                      line-height: 1.2 !important;
                      text-align: center !important;
                    }
                    @media (min-width: 640px) {
                      .stripe-checkout-wrapper button {
                        font-size: 14px !important;
                      }
                    }
                  `
                }} />
                
                <div className="stripe-checkout-wrapper">
                  <StripeCheckout
                    loading={loading}
                    availedTrials={{}}
                    onSelectPlan={handlePlanSelect}
                    customButtonTexts={{
                      basic: isTrial ? 
                        (subscription?.level === 'basic' ? 'Subscribe to Basic (Add Payment Details)' : 'Switch & Subscribe to Basic') :
                        (subscription?.level === 'basic' ? 'Current Plan' : 
                         subscription?.level === 'pro' ? 'Downgrade to Basic' : 'Switch to Basic'),
                      pro: isTrial ?
                        'Subscribe to Pro (Add Payment Details)' :
                        (subscription?.level === 'pro' ? 'Continue with Pro' : 'Upgrade to Pro')
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan Change Option */}
      {!isTrial && showChangePlan && (
        <Card className={cn(
          "mt-4 border",
          theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
        )}>
          <CardHeader>
            <CardTitle className={cn(
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              Change Your Subscription
            </CardTitle>
          </CardHeader>          <CardContent>
            <StripeCheckout
              loading={loading}
              availedTrials={{}}
              onSelectPlan={(planId) => onSelectPlan(planId, false)}
              customButtonTexts={{
                basic: subscription?.level === 'basic' ? 'Current Plan' : 
                       subscription?.level === 'pro' ? 'Downgrade to Basic' : 'Switch to Basic',
                pro: subscription?.level === 'pro' ? 'Current Plan' : 'Upgrade to Pro'
              }}
            />
            
            <Button
              variant="outline"
              onClick={() => setShowChangePlan(false)}
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stripe Secure Payment Note */}
      {isBasicSubscription && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span className="leading-tight text-xs sm:text-xs">
            Secure payment by Stripe. We never store your card details.
          </span>
        </div>
      )}

      {/* PRO Upgrade Section for Basic Users */}
      {isBasicSubscription && (
        <Card className={cn(
          "border-2 shadow-md",
          theme === 'light' 
            ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200' 
            : 'bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border-purple-600'
        )}>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-lg sm:text-xl font-semibold text-purple-800 dark:text-purple-200 leading-tight">
                  Unlock More Features with PRO
                </h4>
                <p className="text-sm text-purple-600 dark:text-purple-300 mt-1">
                  Upgrade for advanced analytics, expenses tracking, and more
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
                  onClick={() => setShowProDetails((v) => !v)}
                  variant="default"
                  size="sm"
                  disabled={upgradeLoading}
                >
                  {showProDetails ? "Hide Details" : "See Benefits"}
                </Button>
              </div>
            </div>
            
            {showProDetails && (
              <div className="space-y-4">
                <div className={cn(
                  "p-3 sm:p-4 rounded-lg border",
                  theme === 'light' 
                    ? 'bg-white/70 border-purple-200' 
                    : 'bg-black/20 border-purple-700'
                )}>
                  <h5 className="font-medium text-purple-800 dark:text-purple-200 mb-3 text-sm sm:text-base">
                    PRO Plan Features:
                  </h5>
                  <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                      <span>Advanced analytics & reporting</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                      <span>Expenses tracking & management</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                      <span>Complete invoice history</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                      <span>Priority support</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                      <span>Unlimited invoices & clients</span>
                    </li>
                  </ul>
                </div>
                <Button
                  className={cn(
                    "w-full bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200",
                    "min-h-[52px] py-4 px-6 text-base font-medium"
                  )}
                  onClick={handleUpgradeToProSubscription}
                  disabled={loading || upgradeLoading}
                  size="lg"
                >
                  {upgradeLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm sm:text-base">Opening Payment Portal...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Star className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-sm sm:text-base font-medium">Upgrade to PRO Plan</span>
                    </div>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActiveSubscriptionDisplay;